import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, FolderOpen, Layers } from 'lucide-react';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_OPTIONS = [
  { value: 'HardHat', label: '🏗️ Construction' },
  { value: 'Factory', label: '🏭 Industrie' },
  { value: 'Truck', label: '🚛 Transport' },
  { value: 'Leaf', label: '🌿 Agriculture' },
  { value: 'Briefcase', label: '💼 Services' },
  { value: 'Building2', label: '🏢 Immobilier' },
  { value: 'Heart', label: '❤️ Santé' },
  { value: 'GraduationCap', label: '🎓 Éducation' }
];

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Bleu', bg: 'bg-blue-100', text: 'text-blue-800' },
  { value: 'green', label: 'Vert', bg: 'bg-green-100', text: 'text-green-800' },
  { value: 'purple', label: 'Violet', bg: 'bg-purple-100', text: 'text-purple-800' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-100', text: 'text-orange-800' },
  { value: 'red', label: 'Rouge', bg: 'bg-red-100', text: 'text-red-800' },
  { value: 'pink', label: 'Rose', bg: 'bg-pink-100', text: 'text-pink-800' }
];

export default function NomenclatureCollectionsManagement() {
  const { getAuthHeader } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'FileText',
    color: 'blue',
    is_active: true
  });

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/nomenclature-collections`, {
        headers: getAuthHeader()
      });
      setCollections(response.data);
    } catch {
      toast.error('Erreur lors du chargement des collections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCollection) {
        await axios.put(
          `${API}/nomenclature-collections/${editingCollection.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Collection mise à jour');
      } else {
        await axios.post(
          `${API}/nomenclature-collections`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Collection créée');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (collection) => {
    setEditingCollection(collection);
    setFormData({
      name: collection.name,
      description: collection.description || '',
      icon: collection.icon || 'FileText',
      color: collection.color || 'blue',
      is_active: collection.is_active
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cette collection ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/nomenclature-collections/${collectionId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Collection archivée');
      fetchCollections();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'FileText',
      color: 'blue',
      is_active: true
    });
    setEditingCollection(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Collections de Nomenclatures</h2>
          <p className="text-gray-600 mt-1">Gérez vos différentes collections de nomenclatures métiers</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle Collection
            </Button>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingCollection ? 'Modifier la collection' : 'Nouvelle collection'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nom de la collection *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: BTP, Industrie, Agriculture..."
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la collection..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="icon">Icône</Label>
                <select
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {ICON_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="color">Couleur</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {COLOR_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: option.value })}
                      className={`p-3 rounded-lg border-2 ${formData.color === option.value ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'} ${option.bg} ${option.text} font-medium`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_active" className="cursor-pointer">Collection active</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Annuler
                </Button>
                <Button type="submit">
                  {editingCollection ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {collections.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="pt-6">
              <div className="text-center text-gray-500 py-4 md:py-8">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Aucune collection trouvée</p>
                <p className="text-sm mt-2">Créez votre première collection pour commencer</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          collections.map((collection) => (
            <Card key={collection.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Layers className="h-6 w-6 text-blue-600" />
                      <CardTitle className="text-lg">{collection.name}</CardTitle>
                    </div>
                    {collection.description && (
                      <p className="text-sm text-gray-600 mt-2">{collection.description}</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant={collection.is_active ? 'default' : 'secondary'}>
                    {collection.is_active ? 'Active' : 'Archivée'}
                  </Badge>
                  <Badge className="bg-slate-600 text-white font-semibold">
                    {collection.categories_count || 0} catégorie(s)
                  </Badge>
                  <Badge className="bg-slate-600 text-white font-semibold">
                    {collection.nomenclatures_count || 0} nomenclature(s)
                  </Badge>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(collection)}
                    className="flex-1"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>
                  {collection.is_active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(collection.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
